import Service from '@ember/service';
import { service } from '@ember/service';
import { waitForPromise } from '@ember/test-waiters';
import type LoggingService from './logger';
import { loadCDPAnalytics } from '../utils/analytics';
import type AnalyticsService from '@ebiz/ember-aditools-analytics/services/aditools-analytics';
import type Owner from '@ember/owner';
//import type SiteService from '@ebiz/ember-aditools-site/services/aditools-site';
import uuid from 'v4-uuid';

const FETCH_POLL_INTERVAL_MS = 1000;

export default class CdpService extends Service {
  @service('aditools-site') declare siteconfig: { [key: string]: string }; //SiteService;
  @service declare logger: LoggingService;
  @service('aditools-analytics') declare analytics: AnalyticsService;
  abortController: AbortController = new AbortController();
  cdpSessionId?: string;

  constructor(owner: Owner) {
    super(owner);
    this.cdpSessionId = uuid();
  }

  willDestroy(): void {
    this.abortController.abort();
  }

  _getFetchPromise<T>(description: string, url: string): Promise<T | null> {
    const reqId = uuid();
    const startTime = new Date();
    loadCDPAnalytics(
      'Start',
      {
        sId: this.cdpSessionId,
        reqId: reqId,
        name: description,
        url: url,
        timeStart: startTime,
      },
      this.analytics,
    );
    const pollHandler = setInterval(() => {
      const endTime = new Date();
      loadCDPAnalytics(
        'In Process',
        {
          sId: this.cdpSessionId,
          reqId: reqId,
          name: description,
          timeStart: startTime,
          timeEnd: endTime,
        },
        this.analytics,
      );
    }, FETCH_POLL_INTERVAL_MS);
    return waitForPromise(
      fetch(url, { signal: this.abortController.signal })
        .then((response) => {
          const endTime = new Date();
          if (response.status !== 200) {
            throw { code: response.status, message: response.statusText };
          }
          clearInterval(pollHandler);
          loadCDPAnalytics(
            'End',
            {
              sId: this.cdpSessionId,
              reqId: reqId,
              name: description,
              timeStart: startTime,
              timeEnd: endTime,
              status: { code: response.status, message: response.statusText },
            },
            this.analytics,
          );
          return response.json() as Promise<T>;
        })
        .catch((error) => {
          clearInterval(pollHandler);
          const endTime = new Date();
          const status = {
            code: error.code ? error.code : -1,
            message: error.message ? error.message : 'Other',
          };
          loadCDPAnalytics(
            'End',
            {
              sId: this.cdpSessionId,
              reqId: reqId,
              name: description,
              timeStart: startTime,
              timeEnd: endTime,
              status: status,
            },
            this.analytics,
          );
          this.logger.error(
            `Load of ${description} at ${url} failed: HTTP Request Failed ${status.code} ${status.message}`,
          );
          return null;
        }),
      description,
    );
  }

  _applyJsonOverrides(item: string, defaultpath: string, forceunique: boolean, override?: string): string {
    const suffix = override && override.startsWith('/testdata') ? 'json' : 'js';
    const url = override ? `${override}/${item}.${suffix}` : `${defaultpath}/${item}.js`;
    return forceunique ? url + '?id=' + new Date().getTime().toString() : url;
  }

  _logCDPUrl(cdpUrl: string | undefined): void {
    if (!cdpUrl) {
      this.logger.error(`CDP URL is undefined and should be configured...`);
    }
  }

  getDeviceModelData<T>(item: string): Promise<T | null> {
    this._logCDPUrl(this.siteconfig['CdpUrl']);
    return this._getFetchPromise<T>(
      'Model data',
      this._applyJsonOverrides(
        item,
        `${this.siteconfig['CdpUrl']}/genericsmodelinfo`,
        false,
        this.siteconfig['ModelUrl'],
      ),
    );
  }

  getCmsData<T>(item: string): Promise<T | null> {
    this._logCDPUrl(this.siteconfig['CdpUrl']);
    return this._getFetchPromise<T>(
      'CMS data',
      this._applyJsonOverrides(
        item,
        `${this.siteconfig['CdpUrl']}/cms/${this.siteconfig['CdpLocale']}`,
        false,
        this.siteconfig['CmsUrl'],
      ),
    );
  }

  getToolsConfig<T>(item: string): Promise<T | null> {
    this._logCDPUrl(this.siteconfig['CdpUrl']);
    return this._getFetchPromise<T>(
      'tools config',
      this._applyJsonOverrides(item, `${this.siteconfig['CdpUrl']}/toolsconfig`, true, this.siteconfig['ConfigUrl']),
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    cdp: CdpService;
  }
}
